import { useCallback, useMemo, useState } from 'react'

import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { ArcadeLinkButton, SegmentedControl, spacing, Stack } from '@cash-design-system/react'
import { SmsDeliveryMechanism } from '@cash-web/protos/squareup/franklin/common/sms_delivery_mechanism.pb'
import { useAuthenticatePasskey } from '@cash-web/shared-auth-feature-main'
import { hasTaxIntent, optionalScopeHeaders, trySetOnboardingContext } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterEmail } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterEmail'
import { useMutationRegisterSms } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterSms'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { isSupportedRegion, PhoneInput, PhoneValue } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { regionConfig } from '@cash-web/shared-region-feature-main'
import { useCountryFromRegionProvider } from '@cash-web/shared-region-ui-context-main'
import { formatPhoneNumberE164, isUndefinedOrEmpty } from '@cash-web/shared-util-formatting'
import { getQueryParam, getSupportedInternalRedirect } from '@cash-web/shared-util-url'
import { isValidEmail } from '@cash-web/shared-util-validation'
import { WebInteractClick, WebViewViewComponent } from '@squareup/customer-data-events-web'

import { getRegisterEmailErrors, getRegisterSMSErrors } from '../../../util/statusErrors'
import { BaseBlocker, SubmitButton } from '../../BaseBlocker/BaseBlocker'
import { EmailForm } from '../BlockerEmail/BlockerEmail'
import { EmailInput } from './EmailInput'
import { LegalText } from './LegalText'

const AliasLink = ({
  trackingEvent,
  children,
  onClick,
}: React.PropsWithChildren<{ trackingEvent: string; onClick: () => void }>) => {
  const logCdfEvent = useLogCashWebCdfEvent()

  function handleClick() {
    onClick()
    logCdfEvent(WebInteractClick({ pageName: 'alias_blocker', componentName: trackingEvent }))
  }

  return (
    <ArcadeLinkButton ariaLabel={trackingEvent} onClick={handleClick}>
      {children}
    </ArcadeLinkButton>
  )
}

export interface Alias {
  type: 'phone' | 'email'
}

export function BlockerAlias(): React.ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { search } = useLocation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const region = useCountryFromRegionProvider()
  const customerRegionCode = useCountryFromRegionProvider()

  const tryLogInWithPasskey = useFeatureFlag('cash-web-passkeys-try-login-with-passkeys') === 'ENABLED'

  const phoneCountryCode = customerRegionCode && isSupportedRegion(customerRegionCode) ? customerRegionCode : 'US'
  const regionConfigEntry = customerRegionCode ? regionConfig[customerRegionCode] : undefined

  const defaultValues = useMemo(
    () => ({
      regionCode: phoneCountryCode,
      phone: '',
      email: '',
      aliasInputType: 'phone' as Alias['type'],
    }),
    [phoneCountryCode]
  )
  const form = useForm<PhoneValue & { aliasInputType: Alias['type'] } & { email: string }>({
    mode: 'onTouched',
    defaultValues,
  })
  const { watch, reset, getValues, handleSubmit } = form
  const phoneValue = watch('phone')
  const emailValue = watch('email')
  const aliasType = watch('aliasInputType')

  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const flow = useFlowContext()
  const { flowToken, clientScenario } = flow.active ?? {}

  const newCustomerRegistration = getQueryParam('su') !== null
  const regionCode = watch('regionCode')
  const [isSkippingPasskeyAndFallingBackToConfirmationCode, setIsSkippingPasskeyAndFallingBackToConfirmationCode] =
    useState(false)

  const { mutateAsync: registerEmail } = useMutationRegisterEmail({
    options: { clientScenario, flowToken, optionalRequestHeaders: optionalScopeHeaders() },
  })
  const { mutateAsync: registerSms } = useMutationRegisterSms({
    options: { clientScenario, flowToken, optionalRequestHeaders: optionalScopeHeaders() },
  })
  async function handleRegisterEmail(email: string, handleFailure?: () => void) {
    await trySetOnboardingContext({ flowToken })
    return await handleBlockerResponse({
      promisedBlockerUpdate: registerEmail(
        {
          email_address: email,
          payment_tokens: flow?.active?.paymentTokens,
        },
        {}
      ),
      errors: getRegisterEmailErrors(genericError, t),
      onFailure: handleFailure,
    })
  }

  async function handleRegisterSms(formattedNumber: string, handleFailure?: () => void) {
    await trySetOnboardingContext({ flowToken })
    return await handleBlockerResponse({
      promisedBlockerUpdate: registerSms({
        sms_number: formattedNumber,
        verification_mechanism: SmsDeliveryMechanism.SMS_TEXT,
        payment_tokens: flow?.active?.paymentTokens,
      }),
      errors: getRegisterSMSErrors(genericError, t),
      onFailure: handleFailure,
    })
  }

  const { authenticatePasskey, isLoading: isPasskeyLoading } = useAuthenticatePasskey({
    onSuccess: (data, flowToken) => {
      flow.updateWithResponse({ response: Promise.resolve(data), flowToken, queriesToInvalidate: [] })
    },
    // On error, skip trying to log in with passkeys and go straight to
    // verification code step.
    onError: () => {
      setIsSkippingPasskeyAndFallingBackToConfirmationCode(true)
      if (aliasType === 'email') {
        handleSubmit(() =>
          handleRegisterEmail(getValues().email, () => {
            setIsSkippingPasskeyAndFallingBackToConfirmationCode(false)
          })
        )()
      }
      if (aliasType === 'phone') {
        handleSubmit(() => {
          const { phone, regionCode } = getValues()
          const formattedNumber = formatPhoneNumberE164(phone, regionCode)
          return handleRegisterSms(formattedNumber, () => setIsSkippingPasskeyAndFallingBackToConfirmationCode(false))
        })()
      }
    },
    onShowPasskeyDialog: () => {
      logCdfEvent(
        WebViewViewComponent({ pageName: 'alias_blocker', componentName: 'Passkey Authenticate System Dialog' })
      )
    },
  })

  const handleSetAliasType = useCallback(
    (type: Alias['type'], prefilledEntry = '') => {
      const resetValues = { ...defaultValues, regionCode, aliasInputType: type }
      if (prefilledEntry) {
        resetValues[type] = prefilledEntry
      }
      reset(resetValues)
    },
    [defaultValues, regionCode, reset]
  )

  const handlePhoneSubmit = async ({ phone, regionCode }: PhoneValue) => {
    let formattedNumber = formatPhoneNumberE164(phone, regionCode)
    if (tryLogInWithPasskey) {
      // Remove the `+1` prefix from US phone numbers
      if (regionCode === 'US' && formattedNumber.length === 12) {
        formattedNumber = formattedNumber.slice(2)
      }
      return authenticatePasskey({ phone: formattedNumber })
    } else {
      return await handleRegisterSms(formattedNumber)
    }
  }

  const handleEmailSubmit = async (data: EmailForm) => {
    const { email } = data
    if (tryLogInWithPasskey) {
      return authenticatePasskey({ email })
    } else {
      return await handleRegisterEmail(email)
    }
  }

  const isTaxLogin = hasTaxIntent()
  const getTitle = () => {
    const internalRedirectParam = getSupportedInternalRedirect()

    if (newCustomerRegistration) {
      if (internalRedirectParam?.includes('resume_order')) {
        return t('Create an account to get your card', { context: 'Title text for resuming a card order.' })
      }

      return t('Create account', {
        context: 'Title text.',
      })
    }

    if (isTaxLogin) {
      return aliasType === 'phone'
        ? t('Enter the phone number you use for Cash App', { context: 'Title text.' })
        : // email
          t('Enter the email you use for Cash App', { context: 'Title text.' })
    }

    switch (internalRedirectParam) {
      case 'support':
        return t('Log in to contact Cash App support', { context: 'Title text.' })
      case '/account/tax-information':
        return getQueryParam('use_business_account')
          ? t('To view your tax info, log in using your Cash for Business account.', { context: 'Title text.' })
          : t('Log in to enter your Tax information', { context: 'Title text.' })
      case '/account/individual-tax-information':
        return t('Log in to enter your Tax information', { context: 'Title text.' })
    }

    if (tryLogInWithPasskey) {
      return t('Log in', { context: 'Title text.' })
    }

    return aliasType === 'phone'
      ? t('Log in using your phone', { context: 'Title text.' })
      : t('Log in using your email', { context: 'Title text.' })
  }

  const createAccountLink = regionConfigEntry?.showCreateAccountLink ? (
    <>
      {' '}
      <Trans
        i18nKey={'New to Cash App? <rawlink>Create account</rawlink>'}
        context="Part of the subtitle text. Prompt for customers without an account, and a Link to Create Account flow."
        components={{
          rawlink: (
            <AliasLink
              key="create account link"
              trackingEvent="Create Account"
              onClick={() => {
                const searchParams = new URLSearchParams(search)
                const queryParams = searchParams.size > 0 ? `?su&${searchParams.toString()}` : '?su'
                navigate(`${window.location.pathname}${queryParams}`, {
                  state: { createAccount: true },
                  replace: true,
                })
              }}
            />
          ),
        }}
      />
    </>
  ) : null

  const loginLink = (
    <>
      {' '}
      <Trans
        i18nKey={'Already have an account? <rawlink>Log in</rawlink>'}
        context="Part of the subtitle text. Prompt for customers without an account, and a Link to Login flow."
        components={{
          rawlink: (
            <AliasLink
              key="login"
              trackingEvent="Login"
              onClick={() => {
                const searchParams = new URLSearchParams(search)
                searchParams.delete('su')
                navigate(window.location.pathname + '?' + searchParams.toString(), {
                  replace: true,
                })
              }}
            />
          ),
        }}
      />
    </>
  )

  const loginRedirect = newCustomerRegistration ? loginLink : createAccountLink

  const isPasskeyAuthenticating =
    tryLogInWithPasskey && (isPasskeyLoading || isSkippingPasskeyAndFallingBackToConfirmationCode)
  const loadingOverride = isPasskeyAuthenticating

  return (
    <Stack space={spacing.defaultContent}>
      <BaseBlocker
        mainText={getTitle()}
        onSubmit={aliasType === 'phone' ? handlePhoneSubmit : handleEmailSubmit}
        form={form}
        buttons={[
          <SubmitButton
            key={`submit-${aliasType}-btn`}
            isLoading={loadingOverride}
            disabled={isUndefinedOrEmpty(aliasType === 'phone' ? phoneValue : emailValue)}
            maxWidth="small"
          />,
        ]}
        disclaimerText={<LegalText region={region || 'US'} isTaxLogin={isTaxLogin} aliasType={aliasType} />}
        footer={loginRedirect}
      >
        <Stack space={spacing.defaultContent}>
          <SegmentedControl
            ariaLabel={t('Select alias type', { context: 'Alias blocker segmented control aria label' })}
            defaultValue={'phone'}
            name="aliasInputType"
            options={[
              {
                label: t('Phone number', { context: 'Option label for phone number input.' }),
                value: 'phone',
              },
              {
                label: t('Email', { context: 'Option label for email input.' }),
                value: 'email',
              },
            ]}
            onChange={() => {
              const nextAliasType = aliasType === 'phone' ? 'email' : 'phone'
              logCdfEvent(
                WebInteractClick({
                  componentName: 'BlockerAlias SegmentedControl',
                  componentType: nextAliasType,
                })
              )
              handleSetAliasType(nextAliasType)
            }}
            value={aliasType}
          />
          {aliasType === 'phone' ? (
            <PhoneInput
              autoFocus
              isLabelHidden
              onPaste={e => {
                const clipboardText = e.clipboardData.getData('text').trim()
                if (isValidEmail(clipboardText)) {
                  e.preventDefault()
                  handleSetAliasType('email', clipboardText)
                }
              }}
              readOnly={loadingOverride}
            />
          ) : (
            <EmailInput handleSetAliasType={handleSetAliasType} loadingOverride={loadingOverride} />
          )}
        </Stack>
      </BaseBlocker>
    </Stack>
  )
}
